import { Link } from "react-router-dom";
import { EmailLink } from "../components/EmailLink";
import { COMPANY } from "../lib/company";

const HELP_TOPICS = [
  {
    icon: "✉",
    title: "Sign-in help",
    body: "Check spam and promotions folders for your one-time code. Codes expire, so request a new one if the first no longer works.",
  },
  {
    icon: "◎",
    title: "Connected accounts",
    body: "Facebook Pages and their linked Instagram professional accounts connect together. YouTube channels connect through Google.",
  },
  {
    icon: "↗",
    title: "Publishing problems",
    body: "Open Posts and select the affected post. Each destination keeps its own status and provider error, so one failure does not hide successful deliveries.",
  },
  {
    icon: "▣",
    title: "Photos and videos",
    body: "If an upload fails, include the file type, approximate size and the screen where it failed. Do not email access tokens or account passwords.",
  },
];

export function Support() {
  const phoneHref = `tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`;

  return (
    <>
      <section className="support-hero">
        <div className="support-hero-inner">
          <p className="hero-badge"><span aria-hidden="true">✦</span> Support</p>
          <h1>Help with <span>{COMPANY.product}</span></h1>
          <p className="lede">
            Get help with sign-in, connected accounts, media uploads, scheduling and
            publishing. This page is public and does not require a {COMPANY.product} account.
          </p>
          <div className="hero-cta support-actions">
            <a
              className="btn-link primary lg"
              href={`mailto:${COMPANY.supportEmail}?subject=${encodeURIComponent(`${COMPANY.product} support request`)}`}
            >
              Email support <span aria-hidden="true">→</span>
            </a>
            <a className="btn-link lg" href={phoneHref}>Call {COMPANY.phone}</a>
          </div>
        </div>
      </section>

      <section className="support-topics">
        <div className="band-inner">
          <div className="support-section-head">
            <p className="eyebrow">Quick help</p>
            <h2>Start with the affected part of the app.</h2>
            <p>These checks resolve the most common problems and help us diagnose the rest.</p>
          </div>
          <div className="support-grid">
            {HELP_TOPICS.map((topic) => (
              <article className="support-card" key={topic.title}>
                <i aria-hidden="true">{topic.icon}</i>
                <h3>{topic.title}</h3>
                <p>{topic.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="support-contact">
        <div className="band-inner support-contact-grid">
          <div>
            <p className="eyebrow">Contact support</p>
            <h2>Tell us what happened.</h2>
            <p>
              Email <EmailLink address={COMPANY.supportEmail} /> from the address on your
              account when possible. Include the app version, device model, Android or iOS
              version, the affected workspace, and the approximate time of the problem.
            </p>
            <p>
              You may attach a screenshot after hiding private information. Never send a
              password, one-time code, social-platform access token or recovery code.
            </p>
            <address>
              <strong>{COMPANY.legalName}</strong><br />
              {COMPANY.address.line1}<br />
              {COMPANY.address.line2}<br />
              {COMPANY.address.city}, {COMPANY.address.state} {COMPANY.address.postalCode}<br />
              {COMPANY.address.country}<br />
              <EmailLink address={COMPANY.supportEmail} /><br />
              <a href={phoneHref}>{COMPANY.phone}</a>
            </address>
          </div>

          <aside className="support-checklist" aria-label="Information to include in a support request">
            <h3>Include in your request</h3>
            <ul>
              <li>What you were trying to do</li>
              <li>The exact message shown on screen</li>
              <li>Whether the problem happens every time</li>
              <li>Your app version and device software version</li>
              <li>The destination involved, without sending its credentials</li>
            </ul>
            <p>
              For a publishing failure, open the post first and include the status shown for
              the affected Facebook, Instagram or YouTube destination.
            </p>
          </aside>
        </div>
      </section>

      <section className="support-resources">
        <div className="band-inner">
          <div className="support-section-head">
            <p className="eyebrow">Account and safety</p>
            <h2>Direct routes for sensitive requests.</h2>
          </div>
          <div className="support-resource-grid">
            <Link to="/data-deletion" className="support-resource">
              <strong>Delete your account and data</strong>
              <span>See the in-app and web request routes, deletion scope and timelines.</span>
            </Link>
            <Link to="/privacy" className="support-resource">
              <strong>Privacy questions</strong>
              <span>Read what data we collect, why we use it and how long we keep it.</span>
            </Link>
            <Link to="/security" className="support-resource">
              <strong>Security issue</strong>
              <span>Learn how credentials are protected and how to report a vulnerability.</span>
            </Link>
            <Link to="/terms#moderation" className="support-resource">
              <strong>Report content or conduct</strong>
              <span>Use the published reporting and grievance process for urgent concerns.</span>
            </Link>
          </div>
          <p className="support-legal-note">
            Support is provided by {COMPANY.legalName}, the operator of {COMPANY.product}.
            Using support does not change the rights or deletion routes described in our{" "}
            <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
          </p>
        </div>
      </section>
    </>
  );
}
